import isNil from 'lodash-es/isNil'
import React from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useDashboardPermissions } from '@components'
import { Toasts } from '@fourthwall/components'
import type { OfferApi } from '@fourthwall/services/dist/OffersApiClient'
import { reverse, routing } from '@utils/routing'

import { isFreeProduct } from '../SingleProduct/SingleProduct.utils'
import { useOfferMutation } from '../hooks/useOfferMutation'
import { useOfferQuery } from '../hooks/useOfferQuery'
import { ProductSettingsView } from './ProductSettingsView'
import type { FormValues, ProductSettingsViewProps } from './types'

export const ProductSettingsContainer = () => {
  const { productId = '' } = useParams<'productId'>()
  const location = useLocation()
  const { hasPermission } = useDashboardPermissions()
  const { offerQuery, setOfferData } = useOfferQuery([productId])
  const { offerMutation } = useOfferMutation({
    onSuccess: () => {
      Toasts.notify('Product has been updated successfully!', { type: 'info' })
    },
  })

  const withMembersOnly = !isNil(offerQuery.data?.requirements?.allowedTiers)
  const freeProduct = offerQuery.data ? isFreeProduct(offerQuery.data) : false
  const maxDigitalFilesSize =
    freeProduct && !withMembersOnly
      ? offerQuery?.data?.config.digitalFileProperties?.maxDigitalFileSizeForFreeOffer
      : offerQuery?.data?.config.digitalFileProperties?.maxDigitalFileSize

  const initialValues = {
    isSoundScanProduct: !!offerQuery.data?.soundScanInfo,
    requirePhoneNumberFromBuyer: !!offerQuery.data?.requirePhoneNumberFromBuyer,
    format: offerQuery.data?.soundScanInfo?.format || 'SINGLE',
    soundScanCode: offerQuery.data?.soundScanInfo?.soundScanCode || '',
    digitalItemsEnabled: !!offerQuery.data?.digitalItems?.length,
    digitalItems:
      offerQuery.data?.digitalItems.map((item) => ({
        id: item.uri,
        url: item.uri,
        name: item.name,
        size: item?.metadata?.fileSize,
      })) || [],
    isCreatorDeclaredCostEnabled:
      !!offerQuery.data &&
      offerQuery.data.manufacturingService === 'MANUFACTURED_BY_CREATOR' &&
      typeof offerQuery.data.metafields === 'object' &&
      offerQuery.data.metafields !== null &&
      'declareCreatorCost' in offerQuery.data.metafields &&
      Boolean((offerQuery.data.metafields as Record<string, unknown>).declareCreatorCost),
  }

  const digitalItemsEditable = !(offerQuery.data?.manufacturingService === 'DIGITAL_ITEM')
  const creatorDeclaredCostEditable =
    offerQuery.data?.manufacturingService === 'MANUFACTURED_BY_CREATOR'

  const getDigitalItemsPayload = (
    values: Pick<FormValues, 'digitalItemsEnabled' | 'digitalItems'>,
  ) => {
    if (!digitalItemsEditable) {
      return offerQuery.data?.digitalItems
    }

    if (!values.digitalItemsEnabled) {
      return []
    }

    return values.digitalItems
      .filter((i) => !i.isUploading && i.url)
      .map((item) => ({ uri: item.url!!, name: item.name }))
  }

  const getPayload: (values: FormValues) => OfferApi.UpdateOfferPayload = ({
    isSoundScanProduct,
    requirePhoneNumberFromBuyer,
    format,
    soundScanCode,
    ...values
  }) => {
    const basePayload = {
      ...offerQuery.data,
      soundScanInfo: isSoundScanProduct ? { format, soundScanCode } : undefined,
      requirePhoneNumberFromBuyer,
      digitalItems: getDigitalItemsPayload(values),
      declareCreatorCost: values.isCreatorDeclaredCostEnabled,
    }

    if (!values.isCreatorDeclaredCostEnabled && offerQuery.data?.variants) {
      return {
        ...basePayload,
        variants: offerQuery.data.variants.map((variant) => ({
          ...variant,
          creatorDeclaredCost: undefined,
        })),
      }
    }

    return basePayload
  }

  const handleSubmit: ProductSettingsViewProps['onSubmit'] = async (values) => {
    await offerMutation.mutateAsync([productId, getPayload(values)], {
      onSuccess: (data) => {
        setOfferData(data) // TODO: decodeHtmlEntities
      },
    })
  }

  return (
    <ProductSettingsView
      backButtonProps={{
        label: offerQuery.data?.name || '',
        to: {
          pathname: reverse(routing.products.all.product.self, { productId }),
          search: location.search,
        },
      }}
      initialValues={initialValues}
      productData={offerQuery.data}
      productName={offerQuery.data?.name}
      isLoading={offerQuery.isLoading}
      isSubmitting={offerMutation.isLoading}
      isFormEditable={hasPermission('products.edit.details')}
      digitalItemsEditable={digitalItemsEditable}
      creatorDeclaredCostEditable={creatorDeclaredCostEditable}
      maxDigitalFilesSize={maxDigitalFilesSize}
      isFreeProduct={freeProduct}
      withMembersOnly={withMembersOnly}
      onSubmit={handleSubmit}
    />
  )
}
